export interface TextTemplateVariable {
  /** Human-readable label shown in the UI. */
  name: string
  /** Raw token inserted into the message, e.g. `CompanyName` -> `{{CompanyName}}`. */
  value: string
}

/**
 * Variables supported in SMS/text templates and custom messages.
 *
 * This must reflect the backend `TextTemplateVariables` enum in
 * `api/src/lib/textTemplate/textTemplate.ts` — the API resolves these tokens
 * per recipient when a message is sent. Keep the two in sync.
 */
export const SMS_TEMPLATE_VARIABLES: TextTemplateVariable[] = [
  { name: 'Company Name', value: 'CompanyName' },
  { name: 'Employee Full Name', value: 'EmployeeFullName' },
  { name: 'Employee First Name', value: 'EmployeeFirstName' },
  { name: 'Employee Phone Number', value: 'EmployeePhoneNumber' },
  { name: 'Applicant Full Name', value: 'ApplicantFullName' },
  { name: 'Applicant First Name', value: 'ApplicantFirstName' },
  { name: 'Application Link', value: 'ApplicationLink' },
  { name: 'Review Link', value: 'ReviewLink' },
  { name: 'Job Listing Name', value: 'JobListingName' },
]

const APPLICANT_FACING_LINKS = ['ApplicationLink', 'ReviewLink']

/**
 * Note-only variables that resolve to an @-mention of the applicant's
 * assignees. Mirrors `NoteMentionVariables` in
 * `api/src/lib/noteTemplate/noteMentionVariables.ts`.
 */
export const NOTE_MENTION_VARIABLES: TextTemplateVariable[] = [
  { name: 'Lead Assignee', value: 'LeadAssignee' },
  { name: 'Member Assignees', value: 'MemberAssignees' },
]

/**
 * Variables offered when authoring a note template. Notes are internal, so the
 * applicant-facing links are left out; everything else resolves through the
 * same API path as SMS templates.
 */
export const NOTE_TEMPLATE_VARIABLES: TextTemplateVariable[] = [
  ...SMS_TEMPLATE_VARIABLES.filter(
    (variable) => !APPLICANT_FACING_LINKS.includes(variable.value)
  ),
  ...NOTE_MENTION_VARIABLES,
]
